import { Box, type DrawerProps, IconButton, Typography } from "@mui/material";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { HStack } from "Components/_Shared/Core/Stack";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import type { CurrentContractBaseInvestmentToolType, CurrentContractBaseSolutionHorizonType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useRef, useState } from "react";
import { type CurrentSolutionFormHandle, CurrentSolutionWrapperForm } from "./Forms/CurrentSolution/CurrentSolutionWrapperForm";

type Props = {
  onClose: () => void;
  investmentToolType: CurrentContractBaseInvestmentToolType;
  solutionId?: string;
  horizonType: CurrentContractBaseSolutionHorizonType;
} & DrawerProps;

export type SubmitCurrentSolutionFormHandle = {
  readonly submitForm: () => void;
  readonly deleteSolution?: () => void;
};

export const CurrentSolutionDrawer: FC<Props> = ({ onClose, investmentToolType, solutionId, horizonType, ...rest }) => {
  const { t } = useResource();
  const currentSolutionFormRef = useRef<SubmitCurrentSolutionFormHandle & CurrentSolutionFormHandle>(null);
  const [contentReady, setContentReady] = useState(false);

  const isEdit = !!solutionId;

  const handleClose = () => {
    const active = document.activeElement as HTMLElement | null;
    if (active && typeof active.blur === "function") active.blur();
    onClose();
  };

  return (
    <Drawer
      {...rest}
      onClose={handleClose}
      ModalProps={{ keepMounted: false, disableRestoreFocus: false }}
      SlideProps={{ onEntered: () => setContentReady(true), onExited: () => setContentReady(false) }}
    >
      <Flex justifyContent="space-between" alignItems="center" p={Spacing.XXL}>
        <Typography variant="h2">
          {isEdit ? t(Resources.AnalysisDetail.GoalsFulfillment.CurrentSolutionEdit) : t(Resources.AnalysisDetail.GoalsFulfillment.Current)}
        </Typography>

        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Flex>

      <DrawerContent>
        <Box px={Spacing.XXL} mb={Spacing.XXL}>
          {contentReady && (
            <CurrentSolutionWrapperForm
              ref={currentSolutionFormRef}
              edit={isEdit}
              solutionID={solutionId}
              horizonType={horizonType}
              initialFormType={investmentToolType}
              onSuccess={onClose}
            />
          )}
        </Box>
      </DrawerContent>

      {isEdit ? (
        <Flex flexDirection="row" justifyContent="space-between" py={Spacing.XXL} px={Spacing.XXL}>
          <Button variant="text" onClick={() => currentSolutionFormRef.current?.deleteSolution?.()} color="error">
            {t(Resources.Common.Delete)}
          </Button>
          <HStack spacing={Spacing.XL}>
            <Button
              onClick={() => handleClose()}
              color="secondary"
              sx={{
                marginRight: Spacing.XL,
              }}
            >
              {t(Resources.Common.Close)}
            </Button>

            <Button type="submit" onClick={() => currentSolutionFormRef.current?.submitForm()}>
              {t(Resources.Common.Save)}
            </Button>
          </HStack>
        </Flex>
      ) : (
        <Flex flexDirection="row" justifyContent="flex-end" py={Spacing.XXL} px={Spacing.XXL}>
          <Button
            onClick={() => handleClose()}
            color="secondary"
            sx={{
              marginRight: Spacing.XL,
            }}
          >
            {t(Resources.Common.Close)}
          </Button>

          <Button type="submit" onClick={() => currentSolutionFormRef.current?.submitForm()}>
            {t(Resources.Common.Create)}
          </Button>
        </Flex>
      )}
    </Drawer>
  );
};
